import { CircularProgress, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useState } from 'react';
import useDebouncedEffect from 'hooks/useDebouncedEffect';

interface AutoCompleteProps<T> {
  disabled?: boolean;
  selectedItem: T | null;
  onChange: (event: any, item: T | null) => void;
  label: string;
  fetchOptions: (inputValue: string) => Promise<T[]>;
  getOptionLabel: (option: T) => string;
}

function AutoCompleteGeneric<T>({
  disabled,
  selectedItem,
  onChange,
  label,
  fetchOptions,
  getOptionLabel,
}: AutoCompleteProps<T>): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<T[]>([]);
  const [open, setOpen] = useState(false);

  useDebouncedEffect(
    () => {
      let active = true;

      if (!open || loading) {
        return undefined;
      }

      (async () => {
        setLoading(true);
        try {
          const data = await fetchOptions(inputValue);
          if (active) {
            setOptions(data);
          }
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      })();

      return () => {
        active = false;
      };
    },
    1000,
    [inputValue, open],
  );

  return (
    <Autocomplete
      disabled={disabled}
      size="small"
      open={open}
      options={options}
      loading={loading}
      inputValue={inputValue}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionSelected={(option, value) =>
        JSON.stringify(option) === JSON.stringify(value)
      }
      getOptionLabel={getOptionLabel}
      loadingText="Carregando..."
      noOptionsText="Nada encontrado"
      value={selectedItem}
      onChange={onChange}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          label={label}
          variant="outlined"
          InputLabelProps={{ style: { fontSize: 13 } }}
          InputProps={{
            ...params.InputProps,
            style: { fontSize: 12 },
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={15} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default AutoCompleteGeneric;
